import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { container } from '@/di';
import { TYPES } from '@/di/tokens';
import { UpdatePlanAction } from '@/application/actions';
import { useToast } from '@/presentation/contexts/ToastContext';
import { Button, Input } from '@/presentation/components/common';
import type { PlanCatalogItem, PlanFeatureKey, PlanFeatureUiGroup } from '@/domain/types/planCatalog';
import {
  PLAN_FEATURE_UI_GROUPS,
  PLAN_FEATURE_UI_GROUP_LABELS,
  PLAN_FEATURE_UI_GROUP_ORDER,
  TIER_LABELS,
} from '@/domain/types/planCatalog';
import type { UpdatePlanCapabilitiesPayload } from '@/presentation/hooks/usePlanCatalog';

interface EditPlanModalProps {
  isOpen: boolean;
  plan: PlanCatalogItem;
  onClose: () => void;
  onSuccess: () => void;
  onUpdateCapabilities: (planId: number, payload: UpdatePlanCapabilitiesPayload) => Promise<unknown>;
}

type TabId = 'datos' | 'capacidades';

export const EditPlanModal: React.FC<EditPlanModalProps> = ({
  isOpen,
  plan,
  onClose,
  onSuccess,
  onUpdateCapabilities,
}) => {
  const { t } = useTranslation();
  const { success, error: showError } = useToast();
  const [tab, setTab] = useState<TabId>('datos');
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({
    nombre: plan.nombre,
    consultorios: plan.consultorios,
    profesionales: plan.profesionales,
    precio: plan.precio,
    precioUsd: plan.precioUsd ?? null,
  });
  const [featureMap, setFeatureMap] = useState<Record<string, boolean>>(() =>
    Object.fromEntries(plan.features.map((f) => [f.key, f.enabled])),
  );
  // Agrupado por qué habilita cada capacidad. Mensajería (app/push/WA) se configura en Conexión.
  const groupedFeatures = useMemo(() => {
    const groups = new Map<PlanFeatureUiGroup, typeof plan.features>();
    for (const feature of plan.features) {
      const group = PLAN_FEATURE_UI_GROUPS[feature.key] ?? 'otros';
      if (group === 'conexion') continue;
      const list = groups.get(group) ?? [];
      list.push(feature);
      groups.set(group, list);
    }
    return PLAN_FEATURE_UI_GROUP_ORDER.filter((g) => g !== 'conexion' && groups.has(g)).map(
      (g) => [g, groups.get(g)!] as const,
    );
  }, [plan.features]);

  const handleSaveDatos = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.nombre.trim()) {
      showError(t('pages.plans.validation.nameRequired'));
      return;
    }
    if (formData.nombre.length > 30) {
      showError(t('pages.plans.validation.nameMaxLength'));
      return;
    }
    if (!formData.consultorios || formData.consultorios < 1) {
      showError(t('pages.plans.validation.consultoriosRequired'));
      return;
    }
    if (!formData.profesionales || formData.profesionales < 1) {
      showError(t('pages.plans.validation.profesionalesRequired'));
      return;
    }
    if (!formData.precio || formData.precio < 0.01) {
      showError(t('pages.plans.validation.priceRequired'));
      return;
    }

    setIsLoading(true);
    try {
      const action = container.get<UpdatePlanAction>(TYPES.UpdatePlanAction);
      await action.execute(plan.id, formData);
      success(t('pages.plans.updatedSuccess'));
      onSuccess();
    } catch (err) {
      showError(err instanceof Error ? err.message : t('common.error'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveFeatures = async () => {
    setIsLoading(true);
    try {
      await onUpdateCapabilities(plan.id, {
        features: Object.entries(featureMap).map(([key, enabled]) => ({
          key: key as PlanFeatureKey,
          enabled,
        })),
      });
      success('Capacidades actualizadas');
      onSuccess();
    } catch (err) {
      showError(err instanceof Error ? err.message : t('common.error'));
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 transition-opacity bg-gray-500/70" onClick={onClose} />

        <div className="inline-block align-bottom bg-white dark:bg-gray-800 rounded-xl text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-3xl sm:w-full">
          <div className="px-5 pt-5 pb-3 border-b border-gray-200 dark:border-gray-700">
            <div className="flex justify-between items-start gap-3">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  {t('pages.plans.edit')} — {plan.nombre}
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
                  Tier actual: {TIER_LABELS[plan.productTier]} · {plan.enabledFeatureCount} capacidades
                </p>
              </div>
              <button
                type="button"
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 text-xl leading-none"
              >
                ×
              </button>
            </div>

            <div className="mt-4 flex gap-1">
              {([
                ['datos', 'Datos comerciales'],
                ['capacidades', 'Capacidades incluidas'],
              ] as const).map(([id, label]) => (
                <button
                  key={id}
                  type="button"
                  onClick={() => setTab(id)}
                  className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
                    tab === id
                      ? 'bg-teal-600 text-white'
                      : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>

          {tab === 'datos' ? (
            <form onSubmit={handleSaveDatos}>
              <div className="px-5 py-4 space-y-4">
                <Input
                  label={t('pages.plans.name')}
                  type="text"
                  value={formData.nombre}
                  onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
                  maxLength={30}
                  required
                />
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <Input
                    label={t('pages.plans.consultorios')}
                    type="number"
                    min="1"
                    value={formData.consultorios}
                    onChange={(e) =>
                      setFormData({ ...formData, consultorios: parseInt(e.target.value, 10) || 1 })
                    }
                    required
                  />
                  <Input
                    label={t('pages.plans.profesionales')}
                    type="number"
                    min="1"
                    value={formData.profesionales}
                    onChange={(e) =>
                      setFormData({ ...formData, profesionales: parseInt(e.target.value, 10) || 1 })
                    }
                    required
                  />
                  <Input
                    label={t('pages.plans.price')}
                    type="number"
                    step="0.01"
                    min="0.01"
                    value={formData.precio}
                    onChange={(e) =>
                      setFormData({ ...formData, precio: parseFloat(e.target.value) || 0 })
                    }
                    required
                  />
                  <Input
                    label={t('pages.plans.priceUsd')}
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.precioUsd ?? ''}
                    placeholder={t('pages.plans.priceUsdPlaceholder')}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        precioUsd: e.target.value === '' ? null : parseFloat(e.target.value) || 0,
                      })
                    }
                  />
                </div>
              </div>
              <div className="bg-slate-50 dark:bg-gray-900/50 px-5 py-3 flex flex-col-reverse sm:flex-row sm:justify-end gap-2">
                <Button type="button" variant="secondary" onClick={onClose}>
                  {t('common.cancel')}
                </Button>
                <Button type="submit" variant="primary" isLoading={isLoading}>
                  {t('common.update')}
                </Button>
              </div>
            </form>
          ) : (
            <div>
              <div className="px-5 py-4 space-y-4 max-h-[60vh] overflow-y-auto">
                {groupedFeatures.map(([group, features]) => (
                  <div key={group}>
                    <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-2">
                      {PLAN_FEATURE_UI_GROUP_LABELS[group]}
                    </h4>
                    <div className="space-y-2">
                      {features.map((feature) => (
                        <label
                          key={feature.key}
                          className="flex items-start gap-3 rounded-lg border border-gray-200 dark:border-gray-700 px-3 py-2 hover:bg-gray-50 dark:hover:bg-gray-700/40 cursor-pointer"
                        >
                          <input
                            type="checkbox"
                            className="mt-1"
                            checked={Boolean(featureMap[feature.key])}
                            onChange={(e) =>
                              setFeatureMap((prev) => ({
                                ...prev,
                                [feature.key]: e.target.checked,
                              }))
                            }
                          />
                          <span>
                            <span className="block text-sm font-medium text-gray-900 dark:text-white">
                              {feature.label}
                            </span>
                            <span className="block text-xs text-gray-500 dark:text-gray-400">
                              {feature.description}
                            </span>
                          </span>
                        </label>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
              <div className="bg-slate-50 dark:bg-gray-900/50 px-5 py-3 flex flex-col-reverse sm:flex-row sm:justify-end gap-2">
                <Button type="button" variant="secondary" onClick={onClose}>
                  {t('common.cancel')}
                </Button>
                <Button type="button" variant="primary" isLoading={isLoading} onClick={handleSaveFeatures}>
                  Guardar capacidades
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
